import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { getImageSrc } from '../../../core/image-utilities';
import { TranslateService } from '@ngx-translate/core';
import { StatsApiService } from '../../../core/stats';
import { formatYAxisNumberScale, getMonthDayShortName } from '../../../core/metrics.helper';
import { SpeedScoreInfo } from './speed-score.service';
import { MetricCardHeader } from '../../metrics';
import { Color, ScaleType } from '@swimlane/ngx-charts';

@Component({
  selector: 'app-speed-score-card',
  templateUrl: './speed-score-card.component.html',
  styleUrls: ['./speed-score-card.component.scss'],
  animations: [
    trigger('visibilityChanged', [
      transition(':enter', [
        style({
          transform: 'scale(0)',
          opacity: 0,
        }),
        animate(
          '500ms',
          style({
            transform: 'scale(1)',
            opacity: 1,
          }),
        ),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SpeedScoreCardComponent {
  deviceSpeedScoreInfo: SpeedScoreInfo;
  hasEnoughData = false;
  xAxisRange: string[];
  yLabel: string;
  schemeType = ScaleType.Ordinal;
  schemeColor: Color = { name: 'speedScore', selectable: false, group: ScaleType.Ordinal, domain: ['#1e88e5'] };
  scoreAgeText: string;

  @Input() loading: boolean;
  @Input() refreshing: boolean;
  @Input() header: MetricCardHeader;
  @Input() cardImage: string;
  @Output() learnButtonClicked = new EventEmitter();
  @Output() improveButtonClicked = new EventEmitter();
  @Output() refreshButtonClicked = new EventEmitter();

  @Input()
  set sourceData(deviceSpeedScoreInfo: SpeedScoreInfo) {
    if (deviceSpeedScoreInfo) {
      this.deviceSpeedScoreInfo = deviceSpeedScoreInfo;
      this.scoreAgeText = this.getScoreAgeText();
      this.yLabel =
        deviceSpeedScoreInfo.device +
        ' ' +
        this.translateService.instant('ANALYTICS.SPEED_SCORE_MODAL.CHART.SPEED_SCORE');
      this.xAxisRange = this.getXAxisTicks(deviceSpeedScoreInfo);
      if (!!this.deviceSpeedScoreInfo && this.deviceSpeedScoreInfo.scoreHistory.length > 0) {
        this.hasEnoughData = this.deviceSpeedScoreInfo.scoreHistory[0].series.length >= 4;
      }
    }
  }

  checkImage = getImageSrc('images/check-circle-green.svg');
  warningImage = getImageSrc('images/warning-yellow.svg');
  arrowUpImage = getImageSrc('images/arrow-drop-up.svg');
  arrowDownImage = getImageSrc('images/arrow-drop-down.svg');
  refreshImage = getImageSrc('images/refresh.svg');
  noEnoughDataImage = getImageSrc('images/trending-up.svg');

  constructor(
    private translateService: TranslateService,
    protected statsApiService: StatsApiService,
  ) {}

  learnButtonEvent($event: MouseEvent): void {
    this.learnButtonClicked.emit($event);
  }

  improveButtonEvent($event: MouseEvent): void {
    this.improveButtonClicked.emit($event);
  }

  refreshButtonEvent($event: MouseEvent): void {
    this.refreshButtonClicked.emit($event);
  }

  formatMonthString(date: Date): string {
    return getMonthDayShortName(date);
  }

  formatYAxisScale(value: number): string {
    return formatYAxisNumberScale(value);
  }

  /**
   * Returns a collection of strings that will be displayed as the ticks of the X axis
   * for displaying the page speed score.
   */
  getXAxisTicks(info: SpeedScoreInfo): string[] {
    if (!!info.scoreHistory && info.scoreHistory.length > 0) {
      return info.scoreHistory[0].series.map((s) => s.name as string);
    }
    return [];
  }

  getScoreAgeText(): string {
    return this.statsApiService.getScoreAgeText(
      new Date().getTime() - new Date(this.deviceSpeedScoreInfo.updateDate).getTime(),
    );
  }
}
